import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@finster/ui";
import { X } from "lucide-react";

import {
  FEEDBACK_DIALOG_DESCRIPTION,
  FEEDBACK_DIALOG_TITLE,
  FEEDBACK_REASONS_BY_VALUE,
} from "./constants";
import type { OutputFeedbackValue } from "./types";

interface OutputFeedbackDialogProps {
  open: boolean;
  value: OutputFeedbackValue;
  reasons: string[];
  pending: boolean;
  /** False while a details write is in flight so dismiss cannot drop unsaved pills. */
  dismissible?: boolean;
  errorMessage?: string | null;
  onOpenChange: (open: boolean) => void;
  onToggleReason: (reasonId: string) => void;
  onSubmit: () => void;
}

const PILL_CLASSES =
  "h-auto min-h-8 cursor-pointer! rounded-md border border-border bg-background px-3 py-1.5 text-left text-xs font-medium text-foreground whitespace-normal hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus";

const PILL_SELECTED_CLASSES = "border-primary bg-primary/10 text-primary hover:bg-primary/10";

/**
 * Office WebView fires mouse down and a delayed click for one press. Handle
 * mouse down, then ignore the follow-up click. Keyboard activation only sends
 * click, so that path still runs the handler.
 */
function usePressHandlers() {
  const handledByMouse = React.useRef(new Set<string>());

  return React.useCallback((key: string, handler: () => void) => {
    return {
      onMouseDown: (event: React.MouseEvent) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        handledByMouse.current.add(key);
        handler();
      },
      onClick: (event: React.MouseEvent) => {
        event.stopPropagation();
        if (handledByMouse.current.has(key)) {
          handledByMouse.current.delete(key);
          return;
        }
        handler();
      },
    };
  }, []);
}

/** Optional reason pills for a rating that has already been stored. */
export const OutputFeedbackDialog: React.FC<OutputFeedbackDialogProps> = ({
  open,
  value,
  reasons,
  pending,
  dismissible = true,
  errorMessage,
  onOpenChange,
  onToggleReason,
  onSubmit,
}) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const press = usePressHandlers();

  React.useEffect(() => {
    if (!open) return undefined;
    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (dismissible) onOpenChange(false);
        return;
      }
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && !pending) {
        event.preventDefault();
        onSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange, onSubmit, pending, dismissible]);

  if (!open || typeof document === "undefined") return null;

  const dismiss = press("dismiss", () => {
    if (!dismissible) return;
    onOpenChange(false);
  });
  const close = press("close", () => {
    if (!dismissible) return;
    onOpenChange(false);
  });
  const submit = press("submit", () => {
    if (pending) return;
    onSubmit();
  });

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[200]">
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-auto absolute inset-0 z-0 bg-black/50",
          dismissible ? "cursor-pointer" : "cursor-default"
        )}
        onMouseDown={dismiss.onMouseDown}
        onClick={dismiss.onClick}
      />
      <div
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="output-feedback-title"
        aria-describedby="output-feedback-description"
        className="pointer-events-auto absolute top-1/2 left-1/2 z-10 w-[min(420px,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2 cursor-default rounded-xl border border-border bg-background p-5 text-foreground shadow-xl outline-none"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="output-feedback-title" className="m-0 text-base font-semibold">
              {FEEDBACK_DIALOG_TITLE}
            </h2>
            <p id="output-feedback-description" className="sr-only">
              {FEEDBACK_DIALOG_DESCRIPTION}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            aria-disabled={!dismissible}
            onMouseDown={close.onMouseDown}
            onClick={close.onClick}
            className={cn(
              "inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus [&_svg]:pointer-events-none",
              dismissible ? "cursor-pointer!" : "cursor-not-allowed!"
            )}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {FEEDBACK_REASONS_BY_VALUE[value].map((reason) => {
            const isSelected = reasons.includes(reason.id);
            const handlers = press(`reason:${reason.id}`, () => {
              if (!dismissible) return;
              onToggleReason(reason.id);
            });

            return (
              <button
                key={reason.id}
                type="button"
                aria-pressed={isSelected}
                aria-disabled={!dismissible}
                onMouseDown={handlers.onMouseDown}
                onClick={handlers.onClick}
                className={cn(
                  PILL_CLASSES,
                  isSelected && PILL_SELECTED_CLASSES,
                  !dismissible && "cursor-not-allowed!"
                )}
              >
                {reason.label}
              </button>
            );
          })}
        </div>

        {errorMessage && (
          <p role="alert" className="mt-3 mb-0 text-[12px] text-destructive">
            {errorMessage}
          </p>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onMouseDown={submit.onMouseDown}
            onClick={submit.onClick}
            aria-busy={pending}
            aria-disabled={pending}
            className={cn(
              "inline-flex h-9 items-center justify-center rounded-md border-0 bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus",
              pending ? "cursor-not-allowed! opacity-60" : "cursor-pointer!"
            )}
          >
            Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
